import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Star, Plus, Calendar, CheckCircle, X, User, Building } from 'lucide-react';
import axios from 'axios';
import RatingStars from './RatingStars';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewDetailsModal = ({ isOpen, onClose, friend, onNewReview }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewReviewForm, setShowNewReviewForm] = useState(false);
  const [error, setError] = useState('');
  
  const [newReview, setNewReview] = useState({
    service_used: '',
    rating: 5,
    review: '',
    would_recommend: true
  });

  useEffect(() => {
    if (isOpen && friend) {
      fetchReviews();
    }
  }, [isOpen, friend]);

  const fetchReviews = async () => {
    if (!friend) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API_URL}/api/friends/${friend.id}/reviews`);
      if (response.data.status === 'success') {
        setReviews(response.data.reviews);
      }
    } catch (error) {
      setError('Failed to load reviews');
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewReviewSubmit = async (e) => {
    e.preventDefault();
    
    if (!newReview.service_used || !newReview.review) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      const reviewData = {
        friend_id: friend.id,
        ...newReview
      };

      await onNewReview(reviewData);
      
      // Reset form
      setNewReview({
        service_used: '',
        rating: 5,
        review: '',
        would_recommend: true
      });
      setShowNewReviewForm(false);
      setError('');
      
      // Refresh reviews
      fetchReviews();
    } catch (error) {
      setError('Failed to submit review');
    }
  };

  const handleClose = () => {
    setShowNewReviewForm(false);
    setNewReview({
      service_used: '',
      rating: 5,
      review: '',
      would_recommend: true
    });
    setError('');
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (!friend) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
              style={{
                background: 'linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-light) 100%)'
              }}
            >
              {friend.name.charAt(0).toUpperCase()}
            </div>
            <div>
              <div className="text-lg font-semibold" style={{color: 'var(--brand-navy)'}}>
                Reviews for {friend.name}
              </div>
              {friend.company && (
                <div className="flex items-center text-sm opacity-70" style={{color: 'var(--brand-navy)'}}>
                  <Building className="w-4 h-4 mr-1" />
                  {friend.company}
                </div>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex-1 overflow-y-auto space-y-4">
          {/* Add New Review Button */}
          {!showNewReviewForm && (
            <Button
              onClick={() => setShowNewReviewForm(true)}
              className="w-full text-white"
              style={{
                background: 'linear-gradient(135deg, var(--brand-tan) 0%, var(--brand-tan-dark) 100%)'
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Write a Review
            </Button>
          )}

          {/* New Review Form */}
          {showNewReviewForm && (
            <div className="bg-gray-50 p-4 rounded-lg border">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold" style={{color: 'var(--brand-navy)'}}>Write New Review</h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowNewReviewForm(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <form onSubmit={handleNewReviewSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="service">Service Used *</Label>
                  <Input
                    id="service"
                    value={newReview.service_used}
                    onChange={(e) => setNewReview(prev => ({...prev, service_used: e.target.value}))}
                    placeholder="e.g., HVAC Repair, Consulting..."
                    className="mt-1"
                    required
                  />
                </div>

                <div>
                  <Label>Rating *</Label>
                  <div className="flex gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-6 h-6 cursor-pointer ${
                          star <= newReview.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                        }`}
                        onClick={() => setNewReview(prev => ({...prev, rating: star}))}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="review">Your Review *</Label>
                  <Textarea
                    id="review"
                    value={newReview.review}
                    onChange={(e) => setNewReview(prev => ({...prev, review: e.target.value}))}
                    placeholder="Share your experience..."
                    className="mt-1"
                    rows={3}
                    required
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="recommend"
                    checked={newReview.would_recommend}
                    onChange={(e) => setNewReview(prev => ({...prev, would_recommend: e.target.checked}))}
                  />
                  <Label htmlFor="recommend">I would recommend {friend.name}</Label>
                </div>

                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 text-white" style={{
                    background: 'linear-gradient(135deg, var(--brand-tan) 0%, var(--brand-tan-dark) 100%)'
                  }}>
                    Submit Review
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowNewReviewForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Reviews List */}
          {loading ? (
            <div className="text-center py-8">
              <div className="text-lg" style={{color: 'var(--brand-navy)'}}>Loading reviews...</div>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8">
              <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-600 mb-2">No reviews yet</h3>
              <p className="text-gray-500">Be the first to review {friend.name}!</p>
            </div>
          ) : (
            <div className="space-y-4">
              <h4 className="font-semibold" style={{color: 'var(--brand-navy)'}}>
                Previous Reviews ({reviews.length})
              </h4>
              
              {reviews.map((review, index) => (
                <div key={review.id || index} className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <div className="font-medium" style={{color: 'var(--brand-navy)'}}>You</div>
                        <div className="text-sm text-gray-500">
                          {formatDate(review.created_at || review.started_date)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <RatingStars rating={review.rating} size="w-4 h-4" />
                      {review.would_recommend && (
                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Recommended
                        </Badge>
                      )}
                    </div>
                  </div>

                  {review.service_used && (
                    <div className="mb-2">
                      <Badge variant="outline" className="text-xs">
                        {review.service_used}
                      </Badge>
                    </div>
                  )}

                  <p className="text-gray-700 leading-relaxed">
                    {review.review}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-4 border-t">
          <Button onClick={handleClose} variant="outline" className="w-full">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReviewDetailsModal;